import api from './api';
import type { User, UserOption } from '@/types/user.types';
import type { ApiResponse } from '@/types/api.types';

export interface ListUsersParams {
  page?: number;
  page_size?: number;
  role?: string;
  group_id?: string;
  is_active?: boolean;
  search?: string;
}

// Mirrors backend CreateUserRequest (backend/internal/users/models.go):
// group_ids required,min=1, primary_group_id required. The console sends no
// password (QA #75): the backend e-mails the user a 設定密碼 link instead.
export interface CreateUserRequest {
  username: string;
  email: string;
  /** Optional (API compatibility); the console never sets it. */
  password?: string;
  role: string;
  group_ids: string[];
  primary_group_id: string;
}

/**
 * POST /users 201 body (QA #75): the user plus whether the 設定密碼 invite
 * mail went out. When it did not, the account exists and invite_error says
 * why — the page offers 寄送設定密碼信 to resend.
 */
export interface CreateUserResponse extends User {
  invite_sent: boolean;
  invite_error?: string;
}

// Mirrors backend UpdateUserRequest — username is not updatable.
export interface UpdateUserRequest {
  email?: string;
  role?: string;
  is_active?: boolean;
  group_ids?: string[];
  primary_group_id?: string;
}

interface ListUsersResponse {
  items: User[];
  pagination: {
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
  };
}

// Backend 實際回傳格式
interface BackendListUsersResponse {
  users: User[];
  total: number;
  page: number;
  size: number;
}

export const userService = {
  async listUsers(params?: ListUsersParams): Promise<ListUsersResponse> {
    const response = await api.get<ApiResponse<BackendListUsersResponse>>('/api/v1/users', { params });
    const raw = response.data.data;
    if (!raw) {
      return { items: [], pagination: { page: 1, page_size: 20, total: 0, total_pages: 0 } };
    }
    const pageSize = raw.size || 20;
    return {
      items: raw.users ?? [],
      pagination: {
        page: raw.page || 1,
        page_size: pageSize,
        total: raw.total || 0,
        total_pages: Math.ceil((raw.total || 0) / pageSize),
      },
    };
  },

  /**
   * QA #77: the quick-find dropdown's list — active accounts in the caller's
   * scope (admin: all, sub_admin: own groups), ordered by username, at most
   * 50 (backend default 20).
   */
  async getOptions(params?: { search?: string; limit?: number }): Promise<UserOption[]> {
    const response = await api.get<ApiResponse<{ options: UserOption[] }>>('/api/v1/users/options', { params });
    return response.data.data?.options ?? [];
  },

  async createUser(data: CreateUserRequest): Promise<CreateUserResponse> {
    const response = await api.post<ApiResponse<CreateUserResponse>>('/api/v1/users', data);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to create user');
  },

  async updateUser(id: string, data: UpdateUserRequest): Promise<User> {
    const response = await api.put<ApiResponse<User>>(`/api/v1/users/${id}`, data);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to update user');
  },

  /**
   * QA #75: e-mail the user a fresh 設定密碼 link (replaces the admin password
   * reset). The backend answers 503 SMTP_UNAVAILABLE / CONSOLE_URL_UNSET with
   * a Chinese reason; the dialog shows it inline, so the global snackbar
   * stays quiet.
   */
  async sendPasswordSetupEmail(id: string): Promise<void> {
    const response = await api.post<ApiResponse>(`/api/v1/users/${id}/password-setup-email`, undefined, {
      skipGlobalError: true,
    });
    if (!response.data.success) {
      throw new Error('Failed to send password setup email');
    }
  },

  /**
   * Make the user change their password at the next login
   * (must_change_password). Errors are shown in the dialog.
   */
  async requirePasswordChange(id: string): Promise<User> {
    const response = await api.post<ApiResponse<User>>(`/api/v1/users/${id}/require-password-change`, undefined, {
      skipGlobalError: true,
    });
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to require password change');
  },

  // ADR-055 (QA #106): lift a login lock.
  async unlock(id: string): Promise<User> {
    const response = await api.post<ApiResponse<User>>(`/api/v1/users/${id}/unlock`, undefined, {
      skipGlobalError: true,
    });
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to unlock user');
  },

  /**
   * Remove the account row for good (admin only). The backend refuses while
   * the account is active (USER_STILL_ACTIVE), still owns backups / watchers /
   * destinations / schedules (USER_HAS_RESOURCES, counts in the error's
   * details) or is the caller's own (SELF_DELETE); the dialog shows those
   * itself, so the global snackbar stays quiet.
   */
  async deleteUserPermanently(id: string): Promise<void> {
    const response = await api.delete<ApiResponse>(`/api/v1/users/${id}/permanent`, {
      skipGlobalError: true,
    });
    if (!response.data.success) {
      throw new Error('Failed to delete user');
    }
  },
};
